/**
 * Korten ELvis visar i pratbubblan när någon håller muspekaren över honom:
 * tips om hur han används, och "Visste du?" med riktiga siffror.
 *
 * Varje tips är kontrollerat mot koden (Hela-läget i AIChat, gemets
 * filtyper i Composer, Shift+Enter, svarsspråket i ai-search, källraden och
 * "Rätta detta svar" i Message). Källorna under svaren är INTE klickbara –
 * lova inte det. Siffrorna kommer
 * ur databasen – ett kort vars siffra saknas eller är 0 tas inte med, så att
 * ELvis aldrig säger något fel eller tråkigt.
 */

export interface ElvisKort {
  sort: 'tips' | 'visste';
  text: string;
}

export interface ElvisSiffror {
  totalt: number;
  unika: number;
  senaste7d: number;
  kallor: number;
  stycken: number;
  kretskampen: number;
  /** Luckor med gap_status 'resolved' – inte de avfärdade. null om hämtningen föll. */
  luckorBesvarade: number | null;
  /** Den mest ställda frågan genom tiderna – bara om just den går att citera, annars null. */
  toppfraga: { text: string; antal: number } | null;
}

export const ELVIS_TIPS: ElvisKort[] = [
  { sort: 'tips', text: 'Välj Hela uppe till höger om du vill ha hela svaret på en gång i stället för ord för ord.' },
  { sort: 'tips', text: 'Klicka på gemet för att bifoga en fil – txt, csv, pdf eller md – så läser jag den också när jag svarar.' },
  { sort: 'tips', text: 'Shift + Enter ger ny rad utan att skicka frågan.' },
  { sort: 'tips', text: 'Klistra gärna in ett engelskt mejl – jag förstår det och svarar på svenska.' },
  { sort: 'tips', text: 'Under varje svar står vilka källor jag använde – så ser du vad svaret bygger på.' },
  { sort: 'tips', text: 'Är något fel i ett svar? Klicka på Rätta detta svar under det, så kan det rättas till nästa gång.' },
];

const tal = (n: number) => n.toLocaleString('sv-SE');

/** Gör om siffrorna till kort. Allt som saknas eller är 0 hoppas över. */
export function visteDuKort(s: ElvisSiffror): ElvisKort[] {
  const kort: ElvisKort[] = [];
  const lagg = (villkor: boolean, text: string) => villkor && kort.push({ sort: 'visste', text });

  lagg(s.totalt > 0 && s.unika > 0, `Jag har svarat på ${tal(s.totalt)} frågor – ${tal(s.unika)} av dem helt unika.`);
  if (s.toppfraga && s.toppfraga.antal > 1) {
    const fraga = s.toppfraga.text.charAt(0).toUpperCase() + s.toppfraga.text.slice(1);
    lagg(true, `Mest ställda frågan hittills: ”${fraga}” – ${tal(s.toppfraga.antal)} gånger.`);
  }
  lagg(s.kallor > 0 && s.stycken > 0, `Jag läser ur ${tal(s.kallor)} källor, uppdelade i ${tal(s.stycken)} stycken.`);
  lagg((s.luckorBesvarade ?? 0) > 0, `Ni har fyllt ${tal(s.luckorBesvarade ?? 0)} kunskapsluckor. Varje en sak jag kan svara på nu.`);
  lagg(s.senaste7d > 1, `${tal(s.senaste7d)} frågor den senaste veckan.`);
  lagg(s.kretskampen > 1, `${tal(s.kretskampen)} omgångar Kretskampen spelade. Vem leder?`);
  return kort;
}

function blanda<T>(lista: T[]): T[] {
  const ut = [...lista];
  for (let i = ut.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [ut[i], ut[j]] = [ut[j], ut[i]];
  }
  return ut;
}

/** Blandar tips och siffror var för sig och varvar dem, så att samma sort inte kommer flera gånger i rad. */
export function blandaKortlek(tips: ElvisKort[], visste: ElvisKort[]): ElvisKort[] {
  const t = blanda(tips);
  const v = blanda(visste);
  const ut: ElvisKort[] = [];
  for (let i = 0; i < Math.max(t.length, v.length); i++) {
    if (v[i]) ut.push(v[i]);
    if (t[i]) ut.push(t[i]);
  }
  return ut;
}
